import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useCopy } from '../../i18n/LocaleContext';
import { flapMatrix, foldAt, turnProgress } from './coverPageTurnGeometry';
import './CoverPageTurn.css';

// Le sommaire et l'ours reprennent mot pour mot des textes validés : les titres des
// rubriques (Home.js) et le pied de page (Footer.js).
const COPY = {
  fr: {
    magazine: 'Le Placard à Doudous', issue: 'Hors-série', rubric: 'Le casting',
    contents: 'Au sommaire',
    toc: [
      ['3', 'Le casting', 'Qui lira avec vous\u00a0?'],
      ['7', 'En coulisses', 'Une matière choisie, un geste qui prend son temps'],
      ['9', 'À lire', 'Lire moins vite'],
      ['12', 'Pratique', 'Mode d’emploi'],
    ],
    imprint: ['Hors-série · Les Naissances d’août', 'Chaque pièce est un petit trésor cousu avec soin dans l’atelier de Margot.', 'Fait main en France.'],
  },
  en: {
    magazine: 'Le Placard à Doudous', issue: 'Special issue', rubric: 'The cast',
    contents: 'In this issue',
    toc: [
      ['3', 'The cast', 'Who’s reading with you?'],
      ['7', 'Behind the scenes', 'A carefully chosen fabric, a craft that takes its time'],
      ['9', 'Worth reading', 'Read a little slower'],
      ['12', 'Practical', 'Good to know'],
    ],
    imprint: ['Special issue · Born in August', 'Each piece is a small treasure, carefully stitched in Margot’s workshop.', 'Handmade in France.'],
  },
};

// Le double d'un bloc de la page, pour l'image seulement : ni identifiant, ni titre,
// ni lien atteignable. Le lecteur d'écran, les tests et le clavier ne voient que l'original.
export function replica(source) {
  const copy = source.cloneNode(true);
  copy.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(heading => {
    const plain = document.createElement('div');
    [...heading.attributes].forEach(({ name, value }) => plain.setAttribute(name, value));
    plain.append(...heading.childNodes);
    heading.replaceWith(plain);
  });
  [copy, ...copy.querySelectorAll('*')].forEach(el => {
    ['id', 'data-testid', 'aria-labelledby', 'aria-describedby', 'fetchpriority'].forEach(name => el.removeAttribute(name));
    if (el.matches('a, button')) el.setAttribute('tabindex', '-1');
    if (el.tagName === 'IMG') el.loading = 'eager';
  });
  copy.inert = true;
  return copy;
}

function Folio({ c, className }) {
  return (
    <div className={`page-turn-folio ${className}`} aria-hidden="true">
      <div className="page-turn-folio-row mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10">
        <span>{c.magazine}<span className="page-turn-folio-issue"> — {c.issue}</span></span>
        <span>{c.rubric}<b>3</b></span>
      </div>
    </div>
  );
}

// Trop bas pour épingler la couverture : elle défile, comme dans Home.css.
export function useShortScreen() {
  const [short, setShort] = useState(() => window.matchMedia('(max-height: 599px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-height: 599px)');
    const update = () => setShort(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return short;
}

// La couverture qui se tourne comme une page de magazine. Au repos, son
// coin bas droit est corné. Au défilement, la couverture reste épinglée le temps d'un
// écran : le coin se soulève, puis toute la page tourne vers la gauche en montrant
// l'intérieur de la couverture (le sommaire), et découvre la première page, le casting,
// qui monte dessous. Sans animation, la couverture défile et le casting garde son folio.
export default function CoverPageTurn() {
  const c = useCopy(COPY);
  const stageRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const short = useShortScreen();
  const live = !reduceMotion && !short;

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const sequence = stage.parentElement;
    const cover = document.getElementById('couverture');
    const casting = document.querySelector('[data-testid="casting-section"]');
    const head = casting?.querySelector('.rubrique-head');
    if (!live || !cover || !head) return undefined;

    const part = selector => stage.querySelector(selector);
    const under = part('.page-turn-side--under'), over = part('.page-turn-side--over');
    const underFlat = under.firstElementChild, overFlat = over.firstElementChild;
    const shadow = part('.page-turn-shadow'), back = part('.page-turn-back');
    const shade = part('.page-turn-shade'), crease = part('.page-turn-crease');
    const front = part('.page-turn-front'), headCopy = part('.page-turn-head');

    sequence.classList.add('is-live');
    let W = 0, H = 0, T = 0, L = 0, top = 0, ear = 0;
    let shown = '', state = '', boost = 0;

    const render = () => {
      const s = window.scrollY - top;
      const p = turnProgress(s, T);
      const turning = s > 0, turned = p >= 1;
      if (`${turning}${turned}` !== state) {
        state = `${turning}${turned}`;
        // La vraie couverture laisse sa place à son double dès que le coin se soulève ;
        // le vrai titre du casting reprend la sienne quand la page est tournée.
        if (turning && !sequence.classList.contains('is-turning')) matchHover();
        sequence.classList.toggle('is-turning', turning);
        sequence.classList.toggle('is-turned', turned);
      }
      const key = `${p}|${boost}`;
      if (key === shown) return;
      shown = key;
      const fold = foldAt(p, W, H, ear * (1 + 0.7 * boost));
      const { x, y } = fold.origin;
      const angle = fold.tilt * 180 / Math.PI;
      const at = `translate(${x}px, ${y}px) rotate(${angle}deg)`;
      const flat = `rotate(${-angle}deg) translate(${-x}px, ${-y}px)`;
      // Le coin rose de la page de dessous ne sert qu'au repos : ensuite, c'est le vrai casting.
      if (!turning) {
        under.style.transform = `${at} translate(0, ${-L}px)`;
        underFlat.style.transform = `translate(0, ${L}px) ${flat}`;
      }
      over.style.transform = `${at} translate(${-L}px, ${-L}px)`;
      overFlat.style.transform = `translate(${L}px, ${L}px) ${flat}`;
      // Plus la page se soulève, plus son ombre s'élargit sur la page de dessous et sur la couverture.
      const lift = Math.sin(Math.PI * p);
      shadow.style.transform = `${at} translate(0, ${-L}px) scaleX(${(ear * 0.7 + 150 * lift) / 100})`;
      shadow.style.opacity = String(Math.min(1, (1 - p) * 5));
      shade.style.opacity = String(lift);
      back.style.transform = `matrix(${flapMatrix(fold, W).join(', ')})`;
      // Le pli vu du verso : même droite, retournée avec la page.
      crease.style.transform = `translate(${W - x}px, ${y}px) rotate(${180 - angle}deg) translate(0, ${-L}px) scaleX(${(ear * 0.6 + 170 * lift) / 100})`;
    };

    // Survolé au moment où la page part, le bouton de la une garde son allure dans le double.
    const matchHover = () => {
      const cta = cover.querySelector('.cover-cta'), twin = front.querySelector('.cover-cta');
      if (!cta || !twin) return;
      const hovered = cta.matches(':hover');
      const style = getComputedStyle(cta);
      twin.style.transform = hovered ? style.transform : '';
      twin.style.backgroundColor = hovered ? style.backgroundColor : '';
    };

    const layout = () => {
      W = stage.clientWidth;
      H = stage.clientHeight;
      T = Math.max(0, sequence.offsetHeight - H);
      // Sa place dans la page, sans le glissement d'entrée de <main> (App.js) : offsetTop ignore les transform.
      top = 0;
      for (let el = sequence; el; el = el.offsetParent) top += el.offsetTop;
      ear = Math.min(50, Math.max(34, W * 0.033));
      L = Math.ceil(Math.hypot(W, H)) + 120;
      stage.style.setProperty('--turn-w', `${W}px`);
      stage.style.setProperty('--turn-h', `${H}px`);
      stage.style.setProperty('--turn-l', `${L}px`);
      // Le titre du casting, à la place exacte qu'il aura quand la page sera tournée.
      const box = head.getBoundingClientRect(), page = casting.getBoundingClientRect();
      headCopy.style.width = `${box.width}px`;
      headCopy.style.transform = `translate(${box.left - stage.getBoundingClientRect().left}px, ${box.top - page.top}px)`;
      shown = '';
      render();
    };

    let copied = 0;
    const copy = () => {
      copied = 0;
      front.replaceChildren(replica(cover));
      headCopy.replaceChildren(replica(head));
      front.querySelectorAll('img').forEach(img => img.decode?.().catch(() => {}));
      layout();
    };
    copy();
    const changed = new MutationObserver(() => { if (!copied) copied = requestAnimationFrame(copy); });
    const watch = { subtree: true, childList: true, characterData: true, attributes: true };
    changed.observe(cover, watch);
    changed.observe(head, watch);

    const resize = new ResizeObserver(layout);
    resize.observe(stage);
    resize.observe(casting);
    let mounted = true;
    document.fonts?.ready.then(() => { if (mounted) layout(); });
    window.addEventListener('resize', layout);

    // Lenis déplace la page dans sa propre image : on suit son événement pour plier la
    // page dans la même image, sans attendre le défilement natif de l'image suivante.
    let lenis = null, quitLenis = null;
    const followLenis = () => {
      if (window.lenis === lenis) return;
      quitLenis?.();
      lenis = window.lenis || null;
      quitLenis = lenis?.on('scroll', render) || null;
    };
    const onScroll = () => { followLenis(); render(); };
    followLenis();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Au clavier, le casting ne s'atteint qu'une fois la page tournée : son titre est alors le vrai.
    // Un clic sur une fiche déjà découverte, lui, ne tourne pas la page sous le doigt.
    let refocus = 0;
    const turnForFocus = () => {
      if (window.scrollY - top >= T) return;
      if (window.lenis) window.lenis.scrollTo(top + T, { immediate: true, force: true });
      else window.scrollTo(0, top + T);
      render();
    };
    const onFocus = event => {
      if (!event.target.matches?.(':focus-visible')) return;
      turnForFocus();
      cancelAnimationFrame(refocus);
      refocus = requestAnimationFrame(turnForFocus);
    };
    casting.addEventListener('focusin', onFocus);

    // Une fois, peu après l'arrivée, le coin corné se soulève un peu, comme une page qui respire.
    let breath = 0;
    const invite = setTimeout(() => {
      const start = performance.now();
      const step = now => {
        const t = (now - start) / 1700;
        boost = t >= 1 || window.scrollY - top > 0 ? 0 : Math.sin(Math.PI * t) ** 2;
        render();
        breath = boost ? requestAnimationFrame(step) : 0;
      };
      if (window.scrollY - top <= 0) breath = requestAnimationFrame(step);
    }, 1600);

    return () => {
      mounted = false;
      clearTimeout(invite);
      cancelAnimationFrame(breath);
      cancelAnimationFrame(copied);
      cancelAnimationFrame(refocus);
      changed.disconnect();
      resize.disconnect();
      quitLenis?.();
      window.removeEventListener('resize', layout);
      window.removeEventListener('scroll', onScroll);
      casting.removeEventListener('focusin', onFocus);
      front.replaceChildren();
      headCopy.replaceChildren();
      sequence.classList.remove('is-live', 'is-turning', 'is-turned');
    };
  }, [live]);

  return (
    <>
      <div ref={stageRef} className="page-turn" aria-hidden="true">
        {/* Dessous : la première page, le casting. Au repos, seul son coin se montre. */}
        <div className="page-turn-side page-turn-side--under">
          <div className="page-turn-flat"><div className="page-turn-fill" /></div>
        </div>
        {/* Son folio et son titre, imprimés : la couverture les cache tant qu'elle est dessus. */}
        <div className="page-turn-page">
          <Folio c={c} className="page-turn-folio--page" />
          <div className="page-turn-head" />
        </div>
        <div className="page-turn-shadow" />
        {/* Dessus : la couverture, et son verso là où elle se rabat. */}
        <div className="page-turn-side page-turn-side--over">
          <div className="page-turn-flat">
            <div className="page-turn-front" />
            <div className="page-turn-back">
              <div className="page-turn-shade" />
              <div className="page-turn-paper">
                <div className="page-turn-contents">
                  <p className="page-turn-contents-title">{c.contents}</p>
                  <ol>
                    {c.toc.map(([page, rubric, title]) => (
                      <li key={page}><b>{page}</b><span>{rubric}</span><em>{title}</em></li>
                    ))}
                  </ol>
                </div>
                <div className="page-turn-imprint">
                  <p className="page-turn-imprint-name">{c.magazine}</p>
                  {c.imprint.map(line => <p key={line}>{line}</p>)}
                </div>
                <p className="page-turn-paper-folio">2</p>
                <div className="page-turn-crease" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Folio c={c} className="page-turn-folio--still" />
    </>
  );
}
